// delimitedProvider.ts
import { httpClient } from 'providers/clients/httpClient';
import { CommonResultType, StatusOnlyResponse } from 'utility/types/dataProvider';

// ------------------------------------------------
// Delimited Service API types
// ------------------------------------------------

export interface DelimitedAIMappingPayload {
  headers: string;
}

export interface DelimitedAIMappingResponse {
  accountNumber?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  postcode?: string;
  expireTime?: string;
  enclosure: string;
  delimiter: string;
}

export interface DelimitedCSVEditPayload {
  row_number: number;
  row_data: string[];
}

export interface DelimitedErrorItem extends Array<string> {
  0: string; // line number
  1: string; // column number
  2: string; // error message
  3: string; // row data
}

export type GetDelimitedAIMappingParams = {
  headers: string;
};

export type GetDelimitedAIMappingResponse = {
  status?: CommonResultType['status'];
  data?: DelimitedAIMappingResponse;
};

export type UploadDelimitedCSVParams = {
  serviceId: string | number;
  file: File;
  testRun?: boolean;
};

export type UploadDelimitedCSVResponse = StatusOnlyResponse;

export type VerifyDelimitedCSVParams = {
  serviceId: string | number;
};

export type VerifyDelimitedCSVResponse = {
  status?: CommonResultType['status'];
  data?: string; // task_id
};

export type GetDelimitedProcessStatusParams = {
  serviceId: string | number;
  processId: string;
};

export type GetDelimitedProcessStatusResponse = {
  status?: CommonResultType['status'];
  data?: 'running' | 'complete';
};

export type GetDelimitedCSVParams = {
  serviceId: string | number;
};

export type GetDelimitedCSVResponse = {
  status?: CommonResultType['status'];
  data?: string;
  headers?: {
    'content-type': 'text/csv';
  };
};

export type GetDelimitedErrorsParams = {
  serviceId: string | number;
};

export type GetDelimitedErrorsResponse = {
  status?: CommonResultType['status'];
  data?: DelimitedErrorItem[] | string; // Can be array of errors OR "too many errors" message
};

export type EditDelimitedCSVParams = {
  serviceId: string | number;
  edits: DelimitedCSVEditPayload[];
};

export type EditDelimitedCSVResponse = StatusOnlyResponse;

export type FinalizeDelimitedCSVParams = {
  serviceId: string | number;
};

export type FinalizeDelimitedCSVResponse = StatusOnlyResponse;

export type ExecuteDelimitedJobParams = {
  serviceId: string | number;
};

export type ExecuteDelimitedJobResponse = {
  status?: CommonResultType['status'];
  data?: string; // job_id
};

// A processed-subscriber record returned by
// /subscription/delimited/{id}/search/. Field names are dynamic and depend on
// the service's column mapping (e.g. uid, email, fname, postcode, expire).
export type DelimitedSubscriberRecord = Record<string, string | number | null>;

export type SearchDelimitedRecordsParams = {
  serviceId: string | number;
  query?: string;
  /** Opaque page token returned by a prior call. Omit for the first page. */
  page?: string;
  limit?: number;
};

export type SearchDelimitedRecordsResponse = {
  status?: CommonResultType['status'];
  data?: {
    records: DelimitedSubscriberRecord[];
    /** Total record count (when the backend provides it). */
    total: number;
    limit: number;
    /** Token to request the next page; null/empty when no further pages. */
    nextPage: string | null;
    /** Token to request the previous page; null/empty on the first page. */
    previousPage: string | null;
    /** Optional explicit field list for column ordering. */
    fields?: string[];
  };
};

export type ExportDelimitedRecordsParams = {
  serviceId: string | number;
  query?: string;
};

export type ExportDelimitedRecordsResponse = {
  status?: CommonResultType['status'];
  data?: {
    exportId: string;
  };
};

export type DownloadDelimitedExportParams = {
  serviceId: string | number;
  exportId: string;
};

export type DownloadDelimitedExportResponse = {
  status?: CommonResultType['status'];
  blob: Blob;
  filename: string;
};

// ------------------------------------------------
// Delimited Service Provider
// ------------------------------------------------

export const delimitedProvider = {
  /**
   * POST /subscription/services/csv/ai_mapping/
   * Gets AI-powered mapping suggestions for CSV headers.
   *
   * @param headers Comma-separated string of CSV headers
   * @returns An object containing the API response status and mapping data.
   */
  getDelimitedAIMapping: async ({
    headers,
  }: GetDelimitedAIMappingParams): Promise<GetDelimitedAIMappingResponse> => {
    try {
      if (!headers) {
        throw new Error('Missing required headers parameter.');
      }

      // Add csv_header as query parameter
      const params = new URLSearchParams({
        csv_header: headers,
      });

      const path = `/subscription/services/csv/ai_mapping/?${params.toString()}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        // Empty body for POST request since csv_header is in query params
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getDelimitedAIMapping] Error:', error);
      throw error;
    }
  },

  /**
   * POST /subscription/services/{id}/csv/upload/
   * Uploads a CSV file for validation and testing.
   *
   * @param serviceId The service ID (integer)
   * @param file The CSV file to upload
   * @param testRun If true, upload CSV to test directory (default: false)
   * @returns An object containing the API response status.
   */
  uploadDelimitedCSV: async ({
    serviceId,
    file,
    testRun = false,
  }: UploadDelimitedCSVParams): Promise<UploadDelimitedCSVResponse> => {
    try {
      if (!serviceId || !file) {
        throw new Error('Missing required parameters.');
      }

      // Ensure serviceId is a number/integer for the path
      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;

      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const path = `/subscription/services/${id}/csv/upload/`;

      // Add query parameter if testRun is true
      const finalPath = testRun ? `${path}?test_run=true` : path;

      const formData = new FormData();
      formData.append('file', file);

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'POST',
        data: formData,
        // Let the browser set the Content-Type with boundary for multipart/form-data
      });

      return {
        status: status === 204 ? 'success' : (data?.status ?? status),
        data: data?.data,
      };
    } catch (error) {
      console.error('[uploadDelimitedCSV] Error:', error);
      throw error;
    }
  },

  /**
   * POST /subscription/services/{id}/csv/verify/
   * Executes a task to generate an error log for the uploaded CSV.
   *
   * @param serviceId The service ID (integer)
   * @returns An object containing the task ID.
   */
  verifyDelimitedCSV: async ({
    serviceId,
  }: VerifyDelimitedCSVParams): Promise<VerifyDelimitedCSVResponse> => {
    try {
      if (!serviceId) {
        throw new Error('Missing required service ID.');
      }

      // Ensure serviceId is a number/integer for the path
      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;

      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const path = `/subscription/services/${id}/csv/verify/`;

      const { data } = await httpClient({
        url: path,
        method: 'POST',
      });

      // Response should be a plain text task_id
      const taskId = typeof data === 'string' ? data : data?.data;

      return {
        status: 'success',
        data: taskId,
      };
    } catch (error) {
      console.error('[verifyDelimitedCSV] Error:', error);
      throw error;
    }
  },

  /**
   * GET /subscription/services/{id}/csv/status/{process_id}/
   * Gets the status of a delimited processing job.
   *
   * @param serviceId The service ID (integer)
   * @param processId The process/job ID to check
   * @returns An object containing the job status.
   */
  getDelimitedProcessStatus: async ({
    serviceId,
    processId,
  }: GetDelimitedProcessStatusParams): Promise<GetDelimitedProcessStatusResponse> => {
    try {
      if (!serviceId || !processId) {
        throw new Error('Missing required parameters.');
      }

      // Ensure serviceId is a number/integer for the path
      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;

      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const path = `/subscription/services/${id}/csv/status/${processId}/`;

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      // Handle the response format: { status: "success", data: { status: "complete" | "running" } }
      const jobStatus = data?.data?.status || data?.status;

      return {
        status: data?.status || 'success',
        data: jobStatus as 'running' | 'complete',
      };
    } catch (error) {
      console.error('[getDelimitedProcessStatus] Error:', error);
      throw error;
    }
  },

  /**
   * GET /subscription/services/{id}/csv/get/
   * Downloads the CSV file being processed.
   *
   * @param serviceId The service ID (integer)
   * @returns An object containing the CSV content.
   */
  getDelimitedCSV: async ({
    serviceId,
  }: GetDelimitedCSVParams): Promise<GetDelimitedCSVResponse> => {
    try {
      if (!serviceId) {
        throw new Error('Missing required service ID.');
      }

      // Ensure serviceId is a number/integer for the path
      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;

      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const path = `/subscription/services/${id}/csv/get/`;

      const { data, headers } = await httpClient({
        url: path,
        method: 'GET',
        headers: {
          Accept: 'text/csv',
        },
      });

      return {
        status: 'success',
        data: data,
        headers: {
          'content-type': headers?.['content-type'] || 'text/csv',
        },
      };
    } catch (error) {
      console.error('[getDelimitedCSV] Error:', error);
      throw error;
    }
  },

  /**
   * GET /subscription/services/{id}/csv/errors/
   * Gets errors from the CSV validation process.
   *
   * @param serviceId The service ID (integer)
   * @returns An object containing validation errors or "too many errors" message.
   */
  getDelimitedErrors: async ({
    serviceId,
  }: GetDelimitedErrorsParams): Promise<GetDelimitedErrorsResponse> => {
    try {
      if (!serviceId) {
        throw new Error('Missing required service ID.');
      }

      // Ensure serviceId is a number/integer for the path
      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;

      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const path = `/subscription/services/${id}/csv/errors/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      // A 400 here means the errors endpoint is not ready yet.
      // Let polling callers retry instead of treating it as a successful empty response.
      if (status === 400) {
        const notReadyError = new Error('Delimited errors are not ready yet.');
        (
          notReadyError as Error & { response?: { status: number; data: unknown } }
        ).response = {
          status,
          data,
        };
        throw notReadyError;
      }

      // CRITICAL: Check if the response is the "too many errors" message
      // The API returns: { status: "success", data: "Too many errors, please upload modified CSV.\n" }
      if (data?.status === 'success' && typeof data?.data === 'string') {
        // Pass the string through directly - the polling hook will handle it
        return {
          status: 'success',
          data: data.data, // This is the "Too many errors..." string
        };
      }

      // Normal case - array of errors
      // The API returns: { status: "success", data: [[row, col, error, data], ...] }
      if (data?.status === 'success' && Array.isArray(data?.data)) {
        return {
          status: 'success',
          data: data.data,
        };
      }

      // Fallback for other response formats
      return {
        status: data?.status ?? status,
        data: data?.data || [],
      };
    } catch (error: any) {
      if (error?.response?.status !== 400) {
        console.error('[getDelimitedErrors] Error:', error);
      }
      throw error;
    }
  },

  /**
   * POST /subscription/services/{id}/csv/edit/
   * Edits CSV data before final processing.
   *
   * @param serviceId The service ID (integer)
   * @param edits Array of row edits to apply
   * @returns An object containing the API response status.
   */
  editDelimitedCSV: async ({
    serviceId,
    edits,
  }: EditDelimitedCSVParams): Promise<EditDelimitedCSVResponse> => {
    try {
      if (!serviceId || !edits) {
        throw new Error('Missing required parameters.');
      }

      // Ensure serviceId is a number/integer for the path
      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;

      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const path = `/subscription/services/${id}/csv/edit/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(edits),
      });

      return {
        status: status === 204 ? 'success' : (data?.status ?? status),
        data: data?.data,
      };
    } catch (error) {
      console.error('[editDelimitedCSV] Error:', error);
      throw error;
    }
  },

  /**
   * POST /subscription/services/{id}/csv/finalize/
   * Finalizes the CSV and moves it to the processing folder.
   *
   * @param serviceId The service ID (integer)
   * @returns An object containing the API response status.
   */
  finalizeDelimitedCSV: async ({
    serviceId,
  }: FinalizeDelimitedCSVParams): Promise<FinalizeDelimitedCSVResponse> => {
    try {
      if (!serviceId) {
        throw new Error('Missing required service ID.');
      }

      // Ensure serviceId is a number/integer for the path
      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;

      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const path = `/subscription/services/${id}/csv/finalize/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
      });

      return {
        status: status === 204 ? 'success' : (data?.status ?? status),
        data: data?.data,
      };
    } catch (error) {
      console.error('[finalizeDelimitedCSV] Error:', error);
      throw error;
    }
  },

  /**
   * POST /subscription/services/{id}/csv/execute/
   * Executes the legacy delimited job.
   *
   * @param serviceId The service ID (integer)
   * @returns An object containing the job ID.
   */
  executeDelimitedJob: async ({
    serviceId,
  }: ExecuteDelimitedJobParams): Promise<ExecuteDelimitedJobResponse> => {
    try {
      if (!serviceId) {
        throw new Error('Missing required service ID.');
      }

      // Ensure serviceId is a number/integer for the path
      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;

      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const path = `/subscription/services/${id}/csv/execute/`;

      const { data } = await httpClient({
        url: path,
        method: 'POST',
      });

      // Response should be a job ID
      const jobId = typeof data === 'string' ? data : data?.data;

      return {
        status: 'success',
        data: jobId,
      };
    } catch (error) {
      console.error('[executeDelimitedJob] Error:', error);
      throw error;
    }
  },

  /**
   * GET /subscription/delimited/{id}/search/
   * Searches the most recently processed subscriber records for a delimited
   * service. Supports a free-text `query` and cursor-style paging via a `page`
   * token returned in the previous response.
   */
  searchDelimitedRecords: async ({
    serviceId,
    query = '',
    page = '',
    limit = 10,
  }: SearchDelimitedRecordsParams): Promise<SearchDelimitedRecordsResponse> => {
    try {
      if (!serviceId && serviceId !== 0) {
        throw new Error('Missing required service ID.');
      }

      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;
      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const params = new URLSearchParams();
      if (query) params.set('query', query);
      if (page) params.set('page', page);
      params.set('limit', String(limit));

      const path = `/subscription/delimited/${id}/search/?${params.toString()}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      // Response shape:
      // { status, data: { total, next, prev, items: [...] } }
      const payload = data?.data ?? {};
      const records = Array.isArray(payload?.items) ? payload.items : [];
      const total =
        typeof payload?.total === 'number' ? payload.total : records.length;
      const nextPage = payload?.next ?? null;
      const previousPage = payload?.prev ?? null;

      return {
        status: data?.status ?? (status === 200 ? 'success' : status),
        data: {
          records,
          total,
          limit,
          nextPage: nextPage ? String(nextPage) : null,
          previousPage: previousPage ? String(previousPage) : null,
          fields: undefined,
        },
      };
    } catch (error) {
      console.error('[searchDelimitedRecords] Error:', error);
      throw error;
    }
  },

  /**
   * GET /subscription/delimited/{id}/search/?export=true
   * Triggers generation of a CSV export of the processed subscriber records
   * matching the given query. The export runs asynchronously; an export_id is
   * returned for use with `downloadDelimitedExport` once the user is notified
   * that the export is ready.
   */
  exportDelimitedRecords: async ({
    serviceId,
    query = '',
  }: ExportDelimitedRecordsParams): Promise<ExportDelimitedRecordsResponse> => {
    try {
      if (!serviceId && serviceId !== 0) {
        throw new Error('Missing required service ID.');
      }

      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;
      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const params = new URLSearchParams();
      if (query) params.set('query', query);
      params.set('export', 'true');

      const path = `/subscription/delimited/${id}/search/?${params.toString()}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const payload = data?.data ?? data ?? {};
      const exportId =
        typeof payload === 'string'
          ? payload
          : (payload?.export_id ?? payload?.exportId ?? '');

      return {
        status: data?.status ?? (status === 200 ? 'success' : status),
        data: {
          exportId: String(exportId ?? ''),
        },
      };
    } catch (error) {
      console.error('[exportDelimitedRecords] Error:', error);
      throw error;
    }
  },

  /**
   * GET /subscription/delimited/{id}/download/{export_id}/
   * Downloads a previously generated CSV export as a Blob. The caller is
   * responsible for triggering the browser download (e.g. via an object URL).
   */
  downloadDelimitedExport: async ({
    serviceId,
    exportId,
  }: DownloadDelimitedExportParams): Promise<DownloadDelimitedExportResponse> => {
    try {
      if (!serviceId && serviceId !== 0) {
        throw new Error('Missing required service ID.');
      }
      if (!exportId) {
        throw new Error('Missing required export ID.');
      }

      const id = typeof serviceId === 'string' ? parseInt(serviceId, 10) : serviceId;
      if (isNaN(id)) {
        throw new Error('Invalid service ID - must be a valid integer.');
      }

      const path = `/subscription/delimited/${id}/download/${encodeURIComponent(exportId)}/`;

      const response = await httpClient({
        url: path,
        method: 'GET',
        responseType: 'blob',
        headers: {
          Accept: 'text/csv',
        },
      });

      const contentDisposition = response.headers?.['content-disposition'];
      let filename = `delimited-${id}-${exportId}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?(?:;|$)/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      return {
        status: 'success',
        blob: response.data,
        filename,
      };
    } catch (error) {
      console.error('[downloadDelimitedExport] Error:', error);
      throw error;
    }
  },
} as const;

export type DelimitedProviderType = typeof delimitedProvider;

// ------------------------------------------------
// Integration with existing data provider
// ------------------------------------------------

/**
 * Extends your existing data provider with delimited service methods.
 *
 * Usage:
 * import { dataProvider } from 'your-existing-provider';
 * import { delimitedProvider } from './delimitedProvider';
 *
 * export const extendedDataProvider = {
 *   ...dataProvider,
 *   ...delimitedProvider,
 * };
 */
